import React from 'react';
import { STORE_INFO, NAV_ITEMS } from '../constants';
import { MapPin, Phone, Mail, Clock, Lock, Globe } from 'lucide-react';

interface FooterProps {
  onNavigate?: (page: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onNavigate }) => {
  return (
    <footer id="contact" className="bg-slate-900 text-slate-300 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* About */}
          <div>
            {/* Logo in Footer */}
            <div className="flex flex-col items-start leading-none mb-6 group cursor-pointer" onClick={() => onNavigate?.('home')}>
              <span className="font-logo text-brand-primary text-xs tracking-[0.2em] uppercase">Borden's</span>
              <span className="font-logo text-white text-3xl tracking-tight -mt-1.5">Bookshoppe</span>
            </div>
            
            <p className="text-sm leading-relaxed mb-6">
              We are passionate about preserving media history. Whether you're a collector or just looking for a good read or a game to relive the past, we have something for everyone.
            </p>

            {/* Areas Served SEO block */}
            <div className="space-y-2 mt-4">
              <h4 className="text-white text-[10px] font-bold uppercase tracking-[0.2em] mb-2 flex items-center gap-2">
                <Globe className="w-3 h-3 text-brand-primary" /> Regional Coverage
              </h4>
              <p className="text-[10px] leading-relaxed text-slate-400">
                Serving Astoria, Macomb, Havana, Canton, Peoria, Springfield, Quincy, Galesburg, Jacksonville, Pekin, and Monmouth.
              </p>
            </div>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white text-lg font-bold font-serif mb-4">Contact Us</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-5 h-5 text-brand-primary shrink-0" />
                <span>{STORE_INFO.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-brand-primary shrink-0" />
                <span>{STORE_INFO.phone}</span>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-brand-primary shrink-0" />
                <a href={`mailto:${STORE_INFO.email}`} className="hover:text-white transition-colors break-all">{STORE_INFO.email}</a>
              </li>
            </ul>
          </div>

          {/* Hours */}
          <div>
            <h3 className="text-white text-lg font-bold font-serif mb-4">Store Hours</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-brand-primary shrink-0" />
                <div>
                  <p className="font-semibold text-white">Mon - Fri</p>
                  <p>{STORE_INFO.hours.weekdays}</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-brand-primary shrink-0" />
                <div>
                  <p className="font-semibold text-white">Weekend</p>
                  <p className="whitespace-pre-line">{STORE_INFO.hours.weekend}</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Map Location */}
          <div className="h-48 bg-slate-800 rounded-lg overflow-hidden relative shadow-inner border border-slate-700">
             <iframe 
               src="https://storage.googleapis.com/maps-solutions-qsbal5oq46/locator-plus/bt6f/locator-plus.html"
               width="100%" 
               height="100%" 
               style={{ border: 0 }} 
               loading="lazy"
               title="Borden's Bookshoppe Location"
             />
          </div>
        </div>
        
        <div className="mt-12 pt-8 border-t border-slate-800 flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
          <div className="space-y-1">
            <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} {STORE_INFO.name}. All rights reserved.</p>
            <nav className="flex flex-wrap justify-center md:justify-start gap-x-2 gap-y-1 text-[10px] uppercase font-bold tracking-widest text-slate-400">
              {NAV_ITEMS.map((item) => (
                <button 
                  key={item.href}
                  onClick={() => onNavigate?.(item.href)}
                  className="hover:text-brand-primary transition-colors py-3 px-2"
                >
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
          
          <div className="text-right">
             <p className="text-[10px] text-slate-400 uppercase tracking-widest leading-relaxed max-w-sm ml-auto">
              A private family-owned business serving Central Illinois and West-Central Illinois. Est. 2025.
             </p>
          </div>
          
          {/* Admin Link */}
          {onNavigate && (
            <button 
              onClick={() => onNavigate('admin')}
              className="p-2 text-slate-400 hover:text-slate-300 transition-colors flex items-center gap-1 text-[10px]"
              title="Staff Login"
            >
              <Lock className="w-3 h-3" /> Staff
            </button>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;